import Service, { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import { objectToQueryString } from 'later/utils/fetch';

import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';
import type { PopupWindowFeatures } from 'shared/lib/popup-instance';
import type PopupManagerService from 'shared/services/shared/popup-manager';

export const META_MARKETING_API_POPUP_NAME = 'meta_marketing_api_oauth';

interface MetaMarketingApiPopupResult {
  new_meta_ad_user_id?: string;
  error?: 'already_connected' | 'auth_missing' | 'auth_failure' | string;
}

export default class MetaMarketingApiAuthService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service('shared/popup-manager') declare popupManager: PopupManagerService;
  @service declare router: RouterService;

  connect = task(async () => {
    const redirectPath = this.router.urlFor('user.profile-refresh-popup', META_MARKETING_API_POPUP_NAME).slice(1);
    // On ember's dev server, `/oauth/*` hits the SPA catch-all; point at the
    // backend origin directly so the OAuth route resolves.
    const isEmberDevServer = window.location.hostname === 'localhost' && window.location.port === '4200';
    const backendOrigin = isEmberDevServer ? 'https://localhost:3000' : '';
    const url = `${backendOrigin}/oauth/meta_marketing_api${objectToQueryString({ redirect_path: redirectPath })}`;

    const popup = this.popupManager.createPopup<MetaMarketingApiPopupResult>(META_MARKETING_API_POPUP_NAME, {
      url
    });

    const parentWidth = window.innerWidth;
    const parentHeight = window.innerHeight;
    const windowFeatures = {
      popup: true,
      width: 800,
      height: 600,
      left: (parentWidth - 800) / 2,
      top: (parentHeight - 600) / 2
    } satisfies PopupWindowFeatures;

    let data: MetaMarketingApiPopupResult | undefined;
    try {
      data = await popup.open({ features: windowFeatures });
    } catch (error) {
      this.errors.log(error);
      this.showConnectFailure();
      return;
    } finally {
      // popup.open() resolves on postMessage but does not close the popup itself.
      if (popup.isOpen) {
        popup.close();
      }
    }

    if (data?.error === 'already_connected') {
      this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.already_connected.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.already_connected.title')
      });
      return;
    }

    if (data?.error) {
      this.showConnectFailure();
      return;
    }

    if (data?.new_meta_ad_user_id) {
      // MetaAdUser is embedded on the user serializer; reload pulls it into the store.
      await this.auth.currentUserModel?.reload();

      this.alerts.success(this.intl.t('account.meta_marketing_api.connect.success.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.success.title')
      });
    }
  });

  disconnect = task(async () => {
    const metaAdUser = this.auth.currentUserModel?.metaAdUser;
    if (!metaAdUser) return;

    const confirmed = window.confirm(this.intl.t('account.meta_marketing_api.disconnect.confirm'));
    if (!confirmed) return;

    try {
      await metaAdUser.destroyRecord();
      // Reload to clear the embedded relationship from the current-user payload.
      await this.auth.currentUserModel?.reload();
      this.alerts.success(this.intl.t('account.meta_marketing_api.disconnect.success.description'), {
        title: this.intl.t('account.meta_marketing_api.disconnect.success.title')
      });
    } catch (error) {
      this.errors.log(error);
      this.alerts.alert(this.intl.t('account.meta_marketing_api.disconnect.failure.description'), {
        title: this.intl.t('account.meta_marketing_api.disconnect.failure.title')
      });
    }
  });

  private showConnectFailure(): void {
    this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.failure.description'), {
      title: this.intl.t('account.meta_marketing_api.connect.failure.title')
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'meta-marketing-api-auth': MetaMarketingApiAuthService;
  }
}
